import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { ModalType } from '../types';

// ─── Replace these three values with your EmailJS credentials ────────────────
const EJS_SERVICE = 'service_5yb573k';
const EJS_TEMPLATE = 'template_ni7dshh';
const EJS_KEY = 'bLkYVRjTJPJkQi-yL';
// ─────────────────────────────────────────────────────────────────────────────

interface ModalProps {
  onClose: () => void;
  onOpenModal?: (type: ModalType) => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.08)',
  color: '#f2ece0',
  fontFamily: 'Montserrat, sans-serif',
  fontSize: 13,
  fontWeight: 300,
  padding: '14px 18px',
  outline: 'none',
  letterSpacing: '0.02em',
  transition: 'border-color 0.2s',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#b89a5a',
  marginBottom: 10,
  fontFamily: 'Montserrat, sans-serif',
};

const stepLabelStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#8a7040',
  marginBottom: 20,
  marginTop: 8,
  display: 'block',
  paddingBottom: 12,
  borderBottom: '1px solid rgba(184,154,90,0.08)',
  fontFamily: 'Montserrat, sans-serif',
};

const FormInput: React.FC<{
  label: string; id: string; type?: string;
  placeholder?: string; value: string; onChange: (v: string) => void;
}> = ({ label, id, type = 'text', placeholder, value, onChange }) => (
  <div style={{ marginBottom: 28 }}>
    <label htmlFor={id} style={labelStyle}>{label}</label>
    <input
      id={id} type={type} placeholder={placeholder} value={value}
      onChange={(e) => onChange(e.target.value)} style={inputStyle}
      onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(184,154,90,0.42)')}
      onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
    />
  </div>
);

const FormSelect: React.FC<{
  label: string; id: string; options: string[];
  value: string; onChange: (v: string) => void;
}> = ({ label, id, options, value, onChange }) => (
  <div style={{ marginBottom: 28 }}>
    <label htmlFor={id} style={labelStyle}>{label}</label>
    <select
      id={id} value={value} onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}
      onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(184,154,90,0.42)')}
      onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
    >
      <option value="" disabled>Select</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const FormTextarea: React.FC<{
  label: string; id: string; placeholder?: string;
  value: string; onChange: (v: string) => void;
}> = ({ label, id, placeholder, value, onChange }) => (
  <div style={{ marginBottom: 28 }}>
    <label htmlFor={id} style={labelStyle}>{label}</label>
    <textarea
      id={id} placeholder={placeholder} value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle, resize: 'vertical', minHeight: 100 }}
      onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(184,154,90,0.42)')}
      onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
    />
  </div>
);

/* Multi-select checkbox group */
const FormMultiSelect: React.FC<{
  label: string; options: string[];
  selected: string[]; onToggle: (v: string) => void;
}> = ({ label, options, selected, onToggle }) => (
  <div style={{ marginBottom: 28 }}>
    <span style={labelStyle}>{label}</span>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
      {options.map((o) => (
        <label key={o} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={selected.includes(o)}
            onChange={() => onToggle(o)}
            style={{ width: 14, height: 14, accentColor: '#b89a5a', cursor: 'pointer', flexShrink: 0, marginTop: 2 }}
          />
          <span style={{ fontSize: 12, fontWeight: 300, color: '#aea89f', letterSpacing: '0.02em', lineHeight: 1.5 }}>{o}</span>
        </label>
      ))}
    </div>
  </div>
);

const SubmitBtn: React.FC<{ label: string; onClick: () => void; loading?: boolean }> = ({ label, onClick, loading }) => (
  <button
    onClick={onClick}
    disabled={loading}
    style={{
      width: '100%', fontFamily: 'Montserrat, sans-serif', fontSize: 10, fontWeight: 600,
      letterSpacing: '0.22em', textTransform: 'uppercase',
      background: loading ? '#8a7040' : '#b89a5a', color: '#0d0c0b',
      padding: '16px 36px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
      marginTop: 8, transition: 'background 0.2s', opacity: loading ? 0.7 : 1,
    }}
    onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#d4b87a'; }}
    onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = loading ? '#8a7040' : '#b89a5a'; }}
  >
    {loading ? 'Sending...' : label}
  </button>
);

const ModalWrapper: React.FC<{ onClose: () => void; wide?: boolean; children: React.ReactNode }> = ({ onClose, wide, children }) => (
  <div
    className="fixed inset-0 z-[1000] flex items-center justify-center p-10"
    style={{ background: 'rgba(5,4,3,0.92)', backdropFilter: 'blur(10px)' }}
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div
      className="modal-animate relative overflow-y-auto"
      style={{
        background: '#181614',
        border: '1px solid rgba(184,154,90,0.16)',
        boxShadow: '0 40px 100px rgba(0,0,0,0.7), inset 0 1px 0 rgba(184,154,90,0.06)',
        maxWidth: wide ? 1000 : 720,
        width: '100%',
        maxHeight: '90vh',
        padding: '56px 52px',
      }}
    >
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 24, right: 28, background: 'none', border: 'none', fontFamily: 'Montserrat, sans-serif', fontSize: 9, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#78736c', cursor: 'pointer', transition: 'color 0.2s' }}
        onMouseEnter={(e) => (e.currentTarget.style.color = '#f2ece0')}
        onMouseLeave={(e) => (e.currentTarget.style.color = '#78736c')}
      >
        ✕ Close
      </button>
      {children}
    </div>
  </div>
);

const SuccessMessage: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div className="text-center py-10">
    <div style={{ width: 40, height: 1, background: '#b89a5a', margin: '0 auto 24px' }} />
    <h3 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 28, fontWeight: 300, color: '#f2ece0', marginBottom: 12 }}>{title}</h3>
    <p style={{ fontSize: 12, fontWeight: 300, color: '#78736c', letterSpacing: '0.04em', lineHeight: 1.8 }}>{body}</p>
  </div>
);

const StepLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={stepLabelStyle}>{children}</span>
);

const sendEmail = (subject: string, message: string, replyTo?: string) =>
  emailjs.send(EJS_SERVICE, EJS_TEMPLATE, { subject, message, reply_to: replyTo ?? '' }, EJS_KEY);

const industries = ['Hospitality', 'Fashion', 'Beauty / Skincare', 'Real Estate', 'Lifestyle / Wellness', 'Food & Beverage', 'Other Premium Brand'];


// ─── OPERATIONS MODAL ─────────────────────────────────────────────────────────
export const OperationsModal: React.FC<ModalProps> = ({ onClose }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: '', business: '', industry: '', email: '', phone: '', teamSize: '',
    currentReality: '', businessStage: '', desiredOutcome: '', readiness: '', finalContext: '',
  });
  const [disorganized, setDisorganized] = useState<string[]>([]);

  const set = (k: string) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (_arr: string[], setArr: React.Dispatch<React.SetStateAction<string[]>>, v: string) =>
    setArr((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);

  const submit = async () => {
    if (!form.name || !form.email || !form.business) {
      alert('Please fill in your name, business name, and email to continue.');
      return;
    }
    setLoading(true);
    try {
      await sendEmail(
        `Operations Diagnostic — ${form.business}`,
        `OPERATIONS DIAGNOSTIC\n\n` +
        `── Step 1: Business Overview ──\n` +
        `Full Name: ${form.name}\nBusiness Name: ${form.business}\nIndustry: ${form.industry}\n` +
        `Email: ${form.email}\nPhone: ${form.phone}\nTeam Size: ${form.teamSize}\n\n` +
        `── Step 2: Main Operational Issues ──\n${disorganized.join(', ') || 'None selected'}\n\n` +
        `── Step 3: Current Operational Reality ──\n${form.currentReality}\n\n` +
        `── Step 4: Business Stage ──\n${form.businessStage}\n\n` +
        `── Step 5: Desired Outcome ──\n${form.desiredOutcome}\n\n` +
        `── Step 6: Readiness ──\n${form.readiness}\n\n` +
        `── Step 7: Final Context ──\n${form.finalContext}`,
        form.email
      );
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please try again or email us directly at contact@hnldigitalluxe.com');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose}>
      {submitted ? (
        <SuccessMessage
          title="Thank you for submitting your Operational Diagnostic."
          body="Your responses have been received by HNL Digital Luxe for strategic review. If there is a fit, the next step may include a clarity call, operational review session, or strategic recommendation."
        />
      ) : (
        <>
          <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#b89a5a', marginBottom: 16, fontFamily: 'Montserrat, sans-serif' }}>
            Operations Diagnostic
          </div>
          <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 32, fontWeight: 300, color: '#f2ece0', marginBottom: 8 }}>
            Fix My Operation System
          </h2>
          <p style={{ fontSize: 11, fontWeight: 300, color: '#78736c', marginBottom: 8, letterSpacing: '0.04em', lineHeight: 1.8 }}>
            A strategic operational review for brands experiencing backend friction, inconsistent execution, workflow confusion, customer handling issues, or growth that is beginning to outpace internal structure.
          </p>
          <p style={{ fontSize: 10, fontWeight: 300, color: '#5a5450', marginBottom: 40, letterSpacing: '0.06em' }}>
            Estimated time: 2–3 minutes.
          </p>

          <StepLabel>Step 1 — Business Overview</StepLabel>
          <FormInput label="Full Name" id="o-name" placeholder="Full name" value={form.name} onChange={set('name')} />
          <FormInput label="Business Name" id="o-biz" placeholder="Your business name" value={form.business} onChange={set('business')} />
          <FormSelect label="Industry" id="o-industry" options={industries} value={form.industry} onChange={set('industry')} />
          <FormInput label="Email Address" id="o-email" type="email" placeholder="your@email.com" value={form.email} onChange={set('email')} />
          <FormInput label="Phone Number" id="o-phone" type="tel" placeholder="+234 000 000 0000" value={form.phone} onChange={set('phone')} />
          <FormSelect label="Team Size" id="o-team"
            options={['Just me', '2–5', '6–15', '16–30', '30+']}
            value={form.teamSize} onChange={set('teamSize')}
          />

          <StepLabel>Step 2 — Main Operational Issue</StepLabel>
          <FormMultiSelect
            label="What currently feels most disorganized inside the business?"
            options={[
              'Team communication', 'Customer follow-up', 'Workflow structure', 'Sales process',
              'Social media workflow', 'Content approvals', 'Staff management', 'Lead handling',
              'Backend organization', 'Reporting systems', 'Scheduling', 'Marketing coordination',
              'Multiple departments not aligned', 'No clear systems', 'Growth is becoming difficult to manage',
            ]}
            selected={disorganized}
            onToggle={(v) => toggle(disorganized, setDisorganized, v)}
          />

          <StepLabel>Step 3 — Current Business Situation</StepLabel>
          <FormSelect
            label="Which statement best describes your current operational reality?"
            id="o-reality"
            options={[
              'Everything feels reactive',
              'Growth is happening faster than our structure',
              'Team members are not aligned properly',
              'Processes are inconsistent',
              'We need better systems and workflows',
              'Operations are affecting customer experience',
            ]}
            value={form.currentReality}
            onChange={set('currentReality')}
          />

          <StepLabel>Step 4 — Business Stage</StepLabel>
          <FormSelect
            label="Which stage best describes your business right now?"
            id="o-stage"
            options={[
              'Growing',
              'Expanding into multiple services',
              'Managing multiple locations',
              'Scaling rapidly',
              'Rebuilding systems internally',
            ]}
            value={form.businessStage}
            onChange={set('businessStage')}
          />

          <StepLabel>Step 5 — Desired Outcome</StepLabel>
          <FormTextarea
            label="What would operating more smoothly look like for your business?"
            id="o-outcome"
            placeholder="Describe the kind of operational clarity, structure, or efficiency you want the business to have."
            value={form.desiredOutcome}
            onChange={set('desiredOutcome')}
          />

          <StepLabel>Step 6 — Readiness</StepLabel>
          <FormSelect
            label="How ready are you to invest in operational restructuring or support?"
            id="o-readiness"
            options={[
              'Ready immediately',
              'Exploring solutions',
              'Need strategic clarity first',
              'Budgeting for later',
            ]}
            value={form.readiness}
            onChange={set('readiness')}
          />

          <StepLabel>Step 7 — Final Context</StepLabel>
          <FormTextarea
            label="What should HNL understand before reviewing your operational structure?"
            id="o-context"
            placeholder="Add anything important about your team, workflow, operations, or growth challenges."
            value={form.finalContext}
            onChange={set('finalContext')}
          />

          <SubmitBtn label="Submit Operational Diagnostic" onClick={submit} loading={loading} />
        </>
      )}
    </ModalWrapper>
  );
};


// ─── MARKETING MODAL ──────────────────────────────────────────────────────────
export const MarketingModal: React.FC<ModalProps> = ({ onClose }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: '', brand: '', email: '', phone: '', handle: '',
    businessStage: '', currentSituation: '', readiness: '', finalContext: '',
  });
  const [challenges, setChallenges] = useState<string[]>([]);
  const [supportNeeded, setSupportNeeded] = useState<string[]>([]);

  const set = (k: string) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (_arr: string[], setArr: React.Dispatch<React.SetStateAction<string[]>>, v: string) =>
    setArr((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);

  const submit = async () => {
    if (!form.name || !form.email || !form.brand) {
      alert('Please fill in your name, brand, and email to continue.');
      return;
    }
    setLoading(true);
    try {
      await sendEmail(
        `Marketing Diagnostic — ${form.brand}`,
        `MARKETING DIAGNOSTIC\n\n` +
        `── Step 1: Brand Overview ──\n` +
        `Full Name: ${form.name}\nBrand / Business Name: ${form.brand}\nEmail: ${form.email}\n` +
        `Phone: ${form.phone}\nWebsite or Instagram: ${form.handle}\n\n` +
        `── Step 2: Business Stage ──\n${form.businessStage}\n\n` +
        `── Step 3: Main Marketing Challenges ──\n${challenges.join(', ') || 'None selected'}\n\n` +
        `── Step 4: Current Situation ──\n${form.currentSituation}\n\n` +
        `── Step 5: Support Needed ──\n${supportNeeded.join(', ') || 'None selected'}\n\n` +
        `── Step 6: Readiness ──\n${form.readiness}\n\n` +
        `── Step 7: Final Context ──\n${form.finalContext}`,
        form.email
      );
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please try again or email us directly at contact@hnldigitalluxe.com');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose}>
      {submitted ? (
        <SuccessMessage
          title="Thank you for submitting your Marketing Diagnostic."
          body="Your responses have been received by HNL Digital Luxe for strategic review. If there is a fit, the next step may include a clarity call, deeper discovery review, or proposal recommendation."
        />
      ) : (
        <>
          <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#b89a5a', marginBottom: 16, fontFamily: 'Montserrat, sans-serif' }}>
            Marketing Diagnostic
          </div>
          <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 32, fontWeight: 300, color: '#f2ece0', marginBottom: 8 }}>
            Analyze My Marketing
          </h2>
          <p style={{ fontSize: 11, fontWeight: 300, color: '#78736c', marginBottom: 8, letterSpacing: '0.04em', lineHeight: 1.8 }}>
            A strategic review for brands experiencing inconsistent visibility, weak conversion, unclear positioning, or marketing that feels active without producing meaningful growth. This diagnostic helps HNL understand where the breakdown may be happening before recommending the next step.
          </p>
          <p style={{ fontSize: 10, fontWeight: 300, color: '#5a5450', marginBottom: 40, letterSpacing: '0.06em' }}>
            Estimated time: 2–3 minutes.
          </p>

          <StepLabel>Step 1 — Brand Overview</StepLabel>
          <FormInput label="Full Name" id="m-name" placeholder="Full name" value={form.name} onChange={set('name')} />
          <FormInput label="Brand / Business Name" id="m-brand" placeholder="Your brand or business name" value={form.brand} onChange={set('brand')} />
          <FormInput label="Email Address" id="m-email" type="email" placeholder="your@email.com" value={form.email} onChange={set('email')} />
          <FormInput label="Phone Number" id="m-phone" type="tel" placeholder="+234 000 000 0000" value={form.phone} onChange={set('phone')} />
          <FormInput label="Website or Instagram Handle" id="m-handle" placeholder="yoursite.com or @handle" value={form.handle} onChange={set('handle')} />

          <StepLabel>Step 2 — Business Stage</StepLabel>
          <FormSelect
            label="Which stage best describes your brand right now?"
            id="m-stage"
            options={[
              'Launching',
              'Growing steadily',
              'Established but inconsistent',
              'Scaling aggressively',
              'Repositioning / restructuring',
            ]}
            value={form.businessStage}
            onChange={set('businessStage')}
          />

          <StepLabel>Step 3 — Main Marketing Challenge</StepLabel>
          <FormMultiSelect
            label="What currently feels weakest in your marketing?"
            options={[
              'Brand visibility', 'Content consistency', 'Sales / conversion', 'Brand positioning',
              'Website performance', 'Paid advertising', 'Lead generation', 'Customer retention',
              'Messaging clarity', 'Marketing structure', 'Not sure yet',
            ]}
            selected={challenges}
            onToggle={(v) => toggle(challenges, setChallenges, v)}
          />

          <StepLabel>Step 4 — Current Situation</StepLabel>
          <FormSelect
            label="Which statement best describes your current marketing situation?"
            id="m-situation"
            options={[
              'We are posting consistently but results are weak',
              'Marketing feels reactive instead of structured',
              'Our visibility is growing but conversion is weak',
              'Our branding feels inconsistent',
              'We need clearer strategy and direction',
              'We have multiple moving parts but nothing feels aligned',
            ]}
            value={form.currentSituation}
            onChange={set('currentSituation')}
          />

          <StepLabel>Step 5 — Support Needed</StepLabel>
          <FormMultiSelect
            label="What type of support are you looking for?"
            options={[
              'Social media management', 'Content creation', 'Paid advertising',
              'Website design / optimization', 'Branding', 'Email marketing',
              'SMS marketing', 'Google Business optimization',
              'Remote content systems', 'Full growth management', 'Strategic advisory',
            ]}
            selected={supportNeeded}
            onToggle={(v) => toggle(supportNeeded, setSupportNeeded, v)}
          />

          <StepLabel>Step 6 — Readiness</StepLabel>
          <FormSelect
            label="How ready are you to invest in structured growth support?"
            id="m-readiness"
            options={[
              'Ready to move immediately',
              'Exploring options',
              'Need strategic clarity first',
              'Budgeting for later',
            ]}
            value={form.readiness}
            onChange={set('readiness')}
          />

          <StepLabel>Step 7 — Final Context</StepLabel>
          <FormTextarea
            label="What should HNL understand before reviewing your brand?"
            id="m-context"
            placeholder="Tell us anything important about your current marketing, growth goals, or frustrations."
            value={form.finalContext}
            onChange={set('finalContext')}
          />

          <SubmitBtn label="Submit Marketing Diagnostic" onClick={submit} loading={loading} />
        </>
      )}
    </ModalWrapper>
  );
};


// ─── WORK WITH US MODAL ───────────────────────────────────────────────────────
export const WorkWithUsModal: React.FC<ModalProps> = ({ onClose }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    brand: '', industry: '', location: '', website: '',
    serviceInterest: '', challenge: '', timeline: '', notes: '',
  });
  const set = (k: string) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.brand || !form.serviceInterest) {
      alert('Please fill in your Brand / Business Name and Service Interest to continue.');
      return;
    }
    setLoading(true);
    try {
      await sendEmail(
        `Work With HNL Digital Luxe — ${form.brand}`,
        `Hello HNL Digital Luxe,\n\nI would like to inquire about working with your team.\n\n` +
        `Brand / Business Name: ${form.brand}\nIndustry: ${form.industry}\nLocation: ${form.location}\n` +
        `Website / Instagram: ${form.website}\nService Interest: ${form.serviceInterest}\n\n` +
        `Current Challenge:\n${form.challenge}\n\nPreferred Timeline: ${form.timeline}\n\n` +
        `Additional Notes:\n${form.notes}\n\nThank you.`
      );
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please try again or email us directly at contact@hnldigitalluxe.com');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose}>
      {submitted ? (
        <SuccessMessage
          title="Enquiry Sent."
          body="Thank you for reaching out. We review every submission personally and will be in touch within 48 hours if there is a strategic fit."
        />
      ) : (
        <>
          <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#b89a5a', marginBottom: 16, fontFamily: 'Montserrat, sans-serif' }}>Work With Us</div>
          <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 32, fontWeight: 300, color: '#f2ece0', marginBottom: 8 }}>Let's Build Something Serious.</h2>
          <p style={{ fontSize: 11, fontWeight: 300, color: '#78736c', marginBottom: 40, letterSpacing: '0.04em', lineHeight: 1.8 }}>
            Tell us about your brand and what you're looking to achieve. We review every enquiry personally — if there is a strategic fit, we will be in touch within 48 hours.
          </p>
          <FormInput label="Brand / Business Name" id="w-brand" placeholder="Your brand or business name" value={form.brand} onChange={set('brand')} />
          <FormSelect label="Industry" id="w-industry" options={industries} value={form.industry} onChange={set('industry')} />
          <FormInput label="Location" id="w-location" placeholder="e.g. Lagos, Nigeria" value={form.location} onChange={set('location')} />
          <FormInput label="Website / Instagram" id="w-website" placeholder="yoursite.com or @handle" value={form.website} onChange={set('website')} />
          <FormSelect label="Service Interest" id="w-service"
            options={[
              'Brand Strategy & Positioning', 'Content & Social Media Management',
              'Paid Growth (Meta, Google, TikTok)', 'Website & Conversion System',
              'Retention & Email Marketing', 'Operations & Growth Diagnostics',
              'Full Growth Partnership', 'Not sure yet — need direction',
            ]}
            value={form.serviceInterest} onChange={set('serviceInterest')}
          />
          <FormTextarea label="Current Challenge" id="w-challenge" placeholder="What is the core problem you need solved? Describe your situation in plain terms." value={form.challenge} onChange={set('challenge')} />
          <FormSelect label="Preferred Timeline" id="w-timeline"
            options={['Immediately', 'Within 2 weeks', 'Within 30 days', 'Next quarter', 'Flexible']}
            value={form.timeline} onChange={set('timeline')}
          />
          <FormTextarea label="Additional Notes" id="w-notes" placeholder="Anything else you'd like us to know before we connect." value={form.notes} onChange={set('notes')} />
          <SubmitBtn label="Send Enquiry" onClick={submit} loading={loading} />
        </>
      )}
    </ModalWrapper>
  );
};


// ─── PACKAGES MODAL ───────────────────────────────────────────────────────────
const packages = [
  { num: '01', title: 'Starter Luxe', tagline: 'Best for individuals, startups, boutique hotels, or lifestyle brands launching online.', features: ['Social Media Management: Instagram, Facebook + TikTok', '12 Static / Carousel / Reels posts per month', 'Caption copywriting and hashtag strategy', 'Monthly analytics report', '1 strategy call per month'], addons: 'Google Business Page setup · Instagram / Facebook ad management · Content creation (one-day shoot) · Email marketing up to 2,000 contacts — 3 emails monthly' },
  { num: '02', title: 'Growth Luxe', tagline: 'For growing brands ready to scale content and paid media.', features: ['Social Media Management: Facebook, Instagram, TikTok, Google Business', '20 posts monthly: reels, carousels, and videos', '2 monthly shoots / video production sessions', 'Ad management across primary platforms', 'Monthly report + mid-month check-in', 'Dedicated account manager'], addons: '' },
  { num: '03', title: 'Elite Luxe', tagline: 'For established hospitality or premium brands needing full-scale visibility.', features: ['Full creative direction and campaign strategy', '30+ monthly deliverables: photos, reels, motion graphics', 'Paid ads across Meta, Snapchat, and Google', 'Influencer collaboration management', 'SEO-optimized Google Business listing', 'Bi-weekly strategy meetings', 'Custom KPI dashboard'], addons: '' },
  { num: '04', title: 'Executive Retainer', tagline: 'For multi-brand accounts or high-volume digital campaigns.', features: ['Dedicated HNL team: strategist, social media manager, copywriter, designer', 'Multi-platform: Instagram, TikTok, Snapchat, Google, LinkedIn', '360° campaign strategy, ads, and community management', 'Event / activation coverage where applicable', 'Priority reporting and high-touch brand support'], addons: '' },
];

export const PackagesModal: React.FC<ModalProps & { onOpenModal: (type: ModalType) => void }> = ({ onClose, onOpenModal }) => (
  <ModalWrapper onClose={onClose} wide>
    <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#b89a5a', marginBottom: 16, fontFamily: 'Montserrat, sans-serif' }}>Service Packages</div>
    <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 32, fontWeight: 300, color: '#f2ece0', marginBottom: 8 }}>HNL Digital Luxe</h2>
    <p style={{ fontSize: 11, fontWeight: 300, color: '#78736c', marginBottom: 0, letterSpacing: '0.04em', lineHeight: 1.8 }}>Flexible, premium packages crafted for brands at different growth stages. All packages begin with a proposal conversation — we don't do off-the-shelf.</p>
    <div className="grid grid-cols-1 md:grid-cols-2 mt-10 mb-12" style={{ gap: 1, background: 'rgba(184,154,90,0.1)' }}>
      {packages.map((pkg) => (
        <div key={pkg.num} className="package-card" style={{ background: '#131210', padding: '40px 36px', transition: 'background 0.25s' }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#1a1612')}
          onMouseLeave={(e) => (e.currentTarget.style.background = '#131210')}
        >
          <span style={{ display: 'inline-block', fontSize: 8, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#8a7040', border: '1px solid rgba(184,154,90,0.18)', padding: '4px 10px', marginBottom: 20, fontFamily: 'Montserrat, sans-serif' }}>Package {pkg.num}</span>
          <h3 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 26, fontWeight: 400, color: '#f2ece0', marginBottom: 10, lineHeight: 1.2 }}>{pkg.title}</h3>
          <p style={{ fontSize: 11, fontWeight: 300, color: '#78736c', marginBottom: 24, letterSpacing: '0.04em', lineHeight: 1.6 }}>{pkg.tagline}</p>
          <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#b89a5a', marginBottom: 14, fontFamily: 'Montserrat, sans-serif' }}>Includes</div>
          <ul className="list-none mb-7">
            {pkg.features.map((f) => (
              <li key={f} className="flex items-start gap-[10px] py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 12, fontWeight: 300, color: '#aea89f', lineHeight: 1.6 }}>
                <span style={{ color: '#8a7040', fontSize: 10, flexShrink: 0, marginTop: 2 }}>—</span>{f}
              </li>
            ))}
          </ul>
          {pkg.addons && (
            <div style={{ fontSize: 11, fontWeight: 300, color: '#78736c', marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(184,154,90,0.08)', lineHeight: 1.9 }}>
              <strong style={{ color: '#aea89f', fontWeight: 400, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: 6, fontFamily: 'Montserrat, sans-serif' }}>Add-ons available</strong>
              {pkg.addons}
            </div>
          )}
          <div className="mt-6">
            <button onClick={() => { onClose(); onOpenModal('proposal'); }}
              style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', background: 'transparent', color: '#b89a5a', padding: '15px 36px', border: '1px solid rgba(184,154,90,0.28)', cursor: 'pointer', transition: 'background 0.2s' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(184,154,90,0.07)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >Request Proposal</button>
          </div>
        </div>
      ))}
    </div>
    <div style={{ borderTop: '1px solid rgba(184,154,90,0.1)', paddingTop: 36 }}>
      <div className="flex items-center gap-3 mb-6" style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.28em', textTransform: 'uppercase', color: '#b89a5a', fontFamily: 'Montserrat, sans-serif' }}>
        <span style={{ width: 24, height: 1, background: '#b89a5a', display: 'inline-block' }} />Available Add-ons
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: 1, background: 'rgba(184,154,90,0.08)' }}>
        {[{ title: 'Website Design & Management', price: 'From' }, { title: 'Influencer & PR Campaigns', price: 'From' }, { title: 'Monthly Email Marketing', price: 'From' }, { title: 'Photography / Videography', price: 'Custom' }].map((a) => (
          <div key={a.title} style={{ background: '#181614', padding: '20px 22px' }}>
            <h5 style={{ fontSize: 11, fontWeight: 500, color: '#f2ece0', marginBottom: 6, lineHeight: 1.4 }}>{a.title}</h5>
            <p style={{ fontSize: 10, color: '#8a7040', fontWeight: 400, letterSpacing: '0.06em' }}>{a.price}</p>
          </div>
        ))}
      </div>
    </div>
  </ModalWrapper>
);


// ─── PROPOSAL MODAL ───────────────────────────────────────────────────────────
export const ProposalModal: React.FC<ModalProps> = ({ onClose }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', brand: '', handle: '', location: '',
    industry: '', package: '', challenge: '', budget: '', timeline: '', notes: '',
  });
  const [services, setServices] = useState<string[]>([]);
  const set = (k: string) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s: string) => setServices((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);

  const submit = async () => {
    if (!form.name || !form.email || !form.brand) { alert('Please fill in your name, brand, and email to continue.'); return; }
    setLoading(true);
    try {
      await sendEmail(
        `Proposal Request — ${form.brand}`,
        `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nBrand: ${form.brand}\n` +
        `Website / Handle: ${form.handle}\nLocation: ${form.location}\nIndustry: ${form.industry}\n` +
        `Services Needed: ${services.join(', ')}\nPreferred Package: ${form.package}\n` +
        `Budget Readiness: ${form.budget}\nTimeline: ${form.timeline}\n\n` +
        `Current Challenge:\n${form.challenge}\n\nAdditional Notes:\n${form.notes}`,
        form.email
      );
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please try again or email us directly at contact@hnldigitalluxe.com');
    } finally {
      setLoading(false);
    }
  };

  const serviceOptions = ['Social media management', 'Content creation', 'Paid advertising', 'Website design / management', 'Branding', 'Email marketing', 'SMS marketing', 'Google Business optimization', 'Remote content direction', 'Full growth management', 'Operations / backend structure'];

  return (
    <ModalWrapper onClose={onClose}>
      {submitted ? (
        <SuccessMessage title="Proposal Request Received." body="Thank you. Your proposal request has been submitted to HNL Digital Luxe. If there is a strategic fit, our team will review your details and outline the next step." />
      ) : (
        <>
          <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#b89a5a', marginBottom: 16, fontFamily: 'Montserrat, sans-serif' }}>Proposal Request</div>
          <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 32, fontWeight: 300, color: '#f2ece0', marginBottom: 8 }}>Request Proposal</h2>
          <p style={{ fontSize: 11, fontWeight: 300, color: '#78736c', marginBottom: 40, letterSpacing: '0.04em', lineHeight: 1.8 }}>Tell us about your brand, your goals, and what you need. We review every submission personally.</p>

          <StepLabel>Step 1 — Brand Information</StepLabel>
          <FormInput label="Full Name" id="p-name" placeholder="Full name" value={form.name} onChange={set('name')} />
          <FormInput label="Email Address" id="p-email" type="email" placeholder="your@email.com" value={form.email} onChange={set('email')} />
          <FormInput label="Phone Number" id="p-phone" type="tel" placeholder="+234 000 000 0000" value={form.phone} onChange={set('phone')} />
          <FormInput label="Brand / Business Name" id="p-brand" placeholder="Brand name" value={form.brand} onChange={set('brand')} />
          <FormInput label="Website or Instagram Handle" id="p-handle" placeholder="yoursite.com or @handle" value={form.handle} onChange={set('handle')} />
          <FormInput label="City / Country" id="p-loc" placeholder="e.g. Lagos, Nigeria" value={form.location} onChange={set('location')} />

          <StepLabel>Step 2 — Business Category</StepLabel>
          <FormSelect label="Industry" id="p-industry" options={['Hospitality', 'Fashion', 'Beauty / Skincare', 'Real Estate', 'Lifestyle', 'E-commerce', 'Other']} value={form.industry} onChange={set('industry')} />

          <StepLabel>Step 3 — What Support Do You Need?</StepLabel>
          <FormMultiSelect
            label="Select all that apply"
            options={serviceOptions}
            selected={services}
            onToggle={toggleService}
          />

          <StepLabel>Step 4 — Preferred Package</StepLabel>
          <FormSelect label="Package Interest" id="p-package" options={['Starter Luxe', 'Growth Luxe', 'Elite Luxe', 'Executive Retainer', 'Not sure yet']} value={form.package} onChange={set('package')} />

          <StepLabel>Step 5 — Current Challenge</StepLabel>
          <FormTextarea label="Main Issue for HNL to Solve" id="p-challenge" placeholder="What is the main issue you want HNL Digital Luxe to solve for your brand?" value={form.challenge} onChange={set('challenge')} />

          <StepLabel>Step 6 — Budget Readiness</StepLabel>
          <FormSelect label="Investment Readiness" id="p-budget" options={['I am ready to invest now', 'I am comparing options', 'I need clarity first', 'I am not sure yet']} value={form.budget} onChange={set('budget')} />

          <StepLabel>Step 7 — Timeline</StepLabel>
          <FormSelect label="When Do You Want to Start?" id="p-timeline" options={['Immediately', 'Within 2 weeks', 'Within 30 days', 'Next quarter']} value={form.timeline} onChange={set('timeline')} />

          <StepLabel>Step 8 — Final Notes</StepLabel>
          <FormTextarea label="Additional Context" id="p-notes" placeholder="Add any extra context that will help us understand the project." value={form.notes} onChange={set('notes')} />

          <SubmitBtn label="Submit Proposal Request" onClick={submit} loading={loading} />
        </>
      )}
    </ModalWrapper>
  );
};